/**
 * Reusable rich text editor based on React Quill.
 * Supports image paste/upload to OBS and hyperlinks.
 */

import { useCallback, useEffect, useMemo, useRef } from 'react';
import ReactQuill, { Quill } from 'react-quill-new';
import 'react-quill-new/dist/quill.snow.css';
import QuillResizeImage from 'quill-resize-image';
import { message } from 'antd';
import { getUploadUrl } from '@/api/storage';
import { getAccessToken } from '@/api/client';

// Register QuillResizeImage module with proper deduplication
// to avoid "Overwriting modules/resize" warnings during HMR
const initQuillModules = (() => {
  let initialized = false;
  return () => {
    if (!initialized) {
      Quill.register('modules/resize', QuillResizeImage);
      initialized = true;
    }
  };
})();

const API_BASE = import.meta.env.VITE_API_BASE_URL || '/api/v1';

/** Build image proxy URL with auth token for <img> tags */
export function imageProxyUrl(objectKey: string): string {
  const token = getAccessToken();
  return `${API_BASE}/storage/image/${objectKey}?token=${token}`;
}

/** Check if HTML content is effectively empty (no text or images) */
export function isContentEmpty(html: string): boolean {
  if (!html) return true;
  if (/<img\s/i.test(html)) return false;
  const stripped = html.replace(/<[^>]*>/g, '').trim();
  return stripped.length === 0;
}

/** Refresh image proxy tokens in stored HTML for display */
export function refreshImageTokens(html: string): string {
  if (!html) return '';
  const imagePattern = new RegExp(
    `${API_BASE.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}/storage/image/([^"?]+)(\\?[^"]*)?`,
    'g'
  );
  return html.replace(imagePattern, (_match, objectKey) => imageProxyUrl(objectKey));
}

/** Upload an image file to OBS and return the image proxy URL */
async function uploadImage(file: File): Promise<string> {
  const { url, object_key } = await getUploadUrl(file.name, file.type);

  // Upload file to OBS using signed URL
  const response = await fetch(url, {
    method: 'PUT',
    headers: { 'Content-Type': file.type },
    body: file
  });

  // Check upload status - fetch() doesn't throw on HTTP errors by default
  if (!response.ok) {
    throw new Error(
      `图片上传失败: ${response.status} ${response.statusText}`
    );
  }

  // Verify ETag exists (basic integrity check)
  const etag = response.headers.get('ETag');
  if (!etag) {
    throw new Error('图片上传失败: 未收到OBS确认');
  }

  return imageProxyUrl(object_key);
}

async function replaceBase64Image(editor: ReturnType<ReactQuill['getEditor']>, base64Src: string) {
  try {
    const res = await fetch(base64Src);
    const blob = await res.blob();
    const ext = blob.type.split('/')[1] || 'png';
    const file = new File([blob], `paste-${Date.now()}.${ext}`, { type: blob.type });
    const proxyUrl = await uploadImage(file);
    const contents = editor.getContents();
    let index = 0;
    for (const op of contents.ops ?? []) {
      if (typeof op.insert === 'string') {
        index += op.insert.length;
      } else if (typeof op.insert === 'object') {
        if ((op.insert as { image?: string }).image === base64Src) {
          editor.deleteText(index, 1);
          editor.insertEmbed(index, 'image', proxyUrl);
          break;
        }
        index += 1;
      }
    }
  } catch {
    message.error('图片上传失败');
  }
}

const QUILL_MODULES = {
  toolbar: [
    ['bold', 'italic', 'underline'],
    ['link', 'image'],
    [{ list: 'ordered' }, { list: 'bullet' }],
    ['clean'],
  ],
  clipboard: { matchVisual: false },
  resize: {
    locale: {
      altTip: '按住alt键比例缩放',
      inputTip: '输入大小后按回车确认',
    },
  },
};

const QUILL_FORMATS = [
  'bold', 'italic', 'underline',
  'link', 'image',
  'list',
];

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  /** Called when uploading state changes */
  onUploadingChange?: (uploading: boolean) => void;
  style?: React.CSSProperties;
}

const RichTextEditor: React.FC<RichTextEditorProps> = ({
  value,
  onChange,
  placeholder = '输入内容... 支持粘贴图片和超链接',
  onUploadingChange,
  style,
}) => {
  const quillRef = useRef<ReactQuill | null>(null);
  const pendingUploadsRef = useRef(0);
  const updateUploading = useCallback((val: boolean) => {
    onUploadingChange?.(val);
  }, [onUploadingChange]);

  const setupEditor = useCallback(() => {
    const editor = quillRef.current?.getEditor();
    if (!editor) return;

    const toolbar = editor.getModule('toolbar') as { addHandler: (type: string, handler: () => void) => void };
    toolbar.addHandler('image', () => {
      const input = document.createElement('input');
      input.setAttribute('type', 'file');
      input.setAttribute('accept', 'image/*');
      input.click();
      input.onchange = async () => {
        const file = input.files?.[0];
        if (!file) return;
        try {
          const proxyUrl = await uploadImage(file);
          const range = editor.getSelection(true);
          editor.insertEmbed(range.index, 'image', proxyUrl);
          editor.setSelection(range.index + 1);
        } catch {
          message.error('图片上传失败');
        }
      };
    });

    editor.on('text-change', (delta: { ops?: Array<{ insert?: string | { image?: string } }> }) => {
      for (const op of delta.ops ?? []) {
        if (typeof op.insert === 'object' && op.insert !== null) {
          const img = (op.insert as { image?: string }).image;
          if (img && img.startsWith('data:')) {
            pendingUploadsRef.current += 1;
            updateUploading(true);
            replaceBase64Image(editor, img).finally(() => {
              pendingUploadsRef.current -= 1;
              if (pendingUploadsRef.current <= 0) {
                pendingUploadsRef.current = 0;
                updateUploading(false);
              }
            });
          }
        }
      }
    });
  }, [updateUploading]);

  useEffect(() => {
    initQuillModules();
    const timer = setTimeout(setupEditor, 100);
    return () => clearTimeout(timer);
  }, [setupEditor]);

  const editorModules = useMemo(() => QUILL_MODULES, []);

  return (
    <ReactQuill
      ref={quillRef}
      theme="snow"
      value={value}
      onChange={onChange}
      modules={editorModules}
      formats={QUILL_FORMATS}
      placeholder={placeholder}
      style={{ background: '#fff', ...style }}
    />
  );
};

export default RichTextEditor;
